// R5.2 — THE SINGLE RENDERING CHOKEPOINT for the §1.2b typed content model (spec §1.1, §1.5).
//
// EVERY human-readable value on the review surface passes through this module. That is not a style preference: it
// is what makes three separate contracts mechanically checkable at once.
//
//   T-COPY-1  Every rendered string resolves to one of SIX closed sources (§7). Each renderer here stamps the
//             source it used onto the element that owns the text node (`data-copy-src` / `data-copy-id`), so the
//             DOM sweep is a walk over text nodes rather than a reading of the components.
//   T-BAN-1   The banned-output registry scans by PROVENANCE (§1.2b). `applicant_verbatim` is exempt; everything
//             else is scanned. The DOM carries the provenance, so the scanner never has to guess which half of a
//             sentence came from the applicant.
//   T-INJ-1   A `{slot}` literal inside applicant text is inert BY CONSTRUCTION (§1.5), because `fillTemplate` is
//             a SINGLE-PASS segmented renderer: it segments the TEMPLATE once and drops typed values into React
//             children. Applicant text is never a template, is never re-scanned, and never becomes a slot.
//
// THE THREE ABSOLUTE RULES OF THIS FILE (§1.5, client half):
//   1. All text renders as React TEXT NODES. `dangerouslySetInnerHTML` never appears in `rubric/`.
//   2. User/applicant text never appears in an attribute, id, href/URL, CSS selector, class name, or style value.
//      Only registered ids (`CS-*`, `RT-*`, `PL-*`) and closed enum names are ever put in an attribute here.
//   3. Templates only ever come from the registry. A CV/ledger-derived string is never used as a template.
import type { ReactNode } from 'react';
import {
  COPY,
  PHRASING_TEMPLATES,
  APPROVED_PLACEHOLDERS,
  APPROVED_PLACEHOLDER_LITERALS,
  type CopyStringId,
} from './copy';
import { BAND_CHIP_STYLE, EVIDENCE_SUMMARY_STYLE, EVIDENCE_QUOTE_STYLE, MUTED_STYLE, SR_ONLY } from './styles';
import { scanSystemText } from './bannedOutput';
// THE FIELD-LABEL REGISTRY, SHARED WITH LAYER 1 (A11Y-3, round 3). An evidence item that is a stored VALUE rather
// than a quoted span renders behind its registered field label, and the label comes from the same `factLabels`
// table the panel builds its editors from — a second table here would be a second opinion about what a field is
// called, and the two would drift the first time either was edited.
import { presentationFor } from '../../../workers/src/lib/factLabels';

/* ============================================================================================================== *
 * THE SIX ALLOWED SOURCES (T-COPY-1's closed list, §7)
 *
 * Named here as a closed union rather than as free strings, so a seventh source cannot be introduced by a typo in
 * one component. The DOM attribute values ARE these names.
 * ============================================================================================================== */

export type CopySource =
  /** (1) A registered Appendix A `CS-*` id, resolved through `COPY`. */
  | 'cs'
  /** (2) A `PC-*` profile-copy id. DORMANT in R5 (R5-OQ-E): no served field is typed `profile_copy` today. */
  | 'pc'
  /** (3) An engine-pre-rendered `RenderedString`, carrying its OWN registered `copy_string_id`. */
  | 'engine-rendered'
  /** (4) Applicant text, verbatim, from a pinned `applicant_verbatim` path or a resolved evidence span. */
  | 'applicant_verbatim'
  /** (5) Text resolved from the registered `PHRASING_TEMPLATES` registry via `recommendations[].template_id`. */
  | 'phrasing_template'
  /** (6) A system-authored literal from the registered `APPROVED_PLACEHOLDER_LITERALS` set (Appendix A.4). */
  | 'placeholder_literal';

/** A slot value, carrying the provenance it must render under — never a bare string. */
export interface TypedSlot {
  source: CopySource;
  /** The registered id, when the source has one. Never applicant text: attributes are id-only (rule 2). */
  id?: string;
  text: string;
}

export type SlotValue = TypedSlot | string;

/* ============================================================================================================== *
 * THE SINGLE-PASS SEGMENTED RENDERER (§1.5)
 * ============================================================================================================== */

export type TemplateSegment =
  | { kind: 'literal'; text: string }
  | { kind: 'slot'; name: string };

/** `{slot}` names are `[a-z0-9_]` in every registered template (Appendix A.1/A.3). */
const SLOT_PATTERN = /\{([A-Za-z0-9_]+)\}/g;

/**
 * Segment a REGISTERED template into literal and slot parts, in ONE pass over the template string.
 *
 * The output is never re-scanned. That is the whole property: a value substituted into segment 2 cannot be read
 * as a slot by segment 3, because segment 3 was decided before any value existed. A `{slot}` literal sitting
 * inside applicant text therefore lands in a text node and stays there (T-INJ-1).
 */
export function segmentTemplate(template: string): TemplateSegment[] {
  const out: TemplateSegment[] = [];
  let last = 0;
  // `matchAll` walks the string once; the regex is never re-applied to substituted output.
  for (const m of template.matchAll(SLOT_PATTERN)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'literal', text: template.slice(last, at) });
    out.push({ kind: 'slot', name: m[1] });
    last = at + m[0].length;
  }
  if (last < template.length) out.push({ kind: 'literal', text: template.slice(last) });
  return out;
}

/**
 * The single-pass fill, for the cases that need a plain string (an `aria-label`, a test assertion).
 *
 * AN UNSUPPLIED SLOT IS LEFT LITERAL, never blanked — the same rule `rubricCopy.ts` documents. A template that
 * silently rendered "assessed across all  entries" would state a fact nobody supplied.
 *
 * IT SCANS EXACTLY WHAT `TemplateText` SCANS (round-2 advisory; T-BAN-2s names BOTH helpers). This function used
 * to scan nothing at all — neither the template nor the slot values — which made it a second, unscanned path to
 * the screen for precisely the strings the banned-output registry exists to stop. It is not on the production
 * render path today, and that is the whole reason to close it now: an `aria-label` call site added later would
 * inherit an exemption nobody chose. Both helpers now call the same two scans in the same order, over the same
 * inputs, with the same `applicant_verbatim` exemption.
 *
 * FAIL-CLOSED MEANS THE EMPTY STRING HERE. `TemplateText` returns `null` and renders nothing; the string-shaped
 * equivalent is `''`, so a violating template or slot yields no text rather than half a sentence. A caller that
 * needs to distinguish "empty" from "refused" should render through `TemplateText`, which is the render path.
 */
export function fillTemplate(
  template: string,
  slots: Readonly<Record<string, SlotValue>> = {},
  /** The registered id this template came from, for the scanner's own reporting — same argument `TemplateText` takes. */
  id?: string,
  /** The provenance bare-string slots inherit, exactly as in `TemplateText`. Defaults to system-authored copy. */
  source: CopySource = 'cs',
  defaultSlotSource?: CopySource,
): string {
  if (!scanSystemText(template, id).ok) return '';
  for (const v of Object.values(slots)) {
    if (!scanSlot(typeSlot(v, source, id, defaultSlotSource))) return '';
  }
  return segmentTemplate(template)
    .map((seg) => {
      if (seg.kind === 'literal') return seg.text;
      const v = slots[seg.name];
      if (v === undefined) return `{${seg.name}}`;
      return typeof v === 'string' ? v : v.text;
    })
    .join('');
}

/* ============================================================================================================== *
 * THE TYPED RENDERERS — one per provenance class (§1.2b)
 * ============================================================================================================== */

interface TemplateTextProps {
  source: CopySource;
  id?: string;
  template: string;
  slots?: Readonly<Record<string, SlotValue>>;
  /** Slot values that carry NO explicit provenance inherit the template's own. */
  defaultSlotSource?: CopySource;
}

/**
 * Resolve a slot value to its TYPED form — the one place a bare string acquires a provenance.
 *
 * A bare string inherits `defaultSlotSource ?? source`, which is the template's own provenance unless the caller
 * says otherwise. That inheritance is convenience, never an exemption: `scanSlot` below decides scanning from the
 * RESULT of this resolution, so a slot can only escape the scanner by being explicitly typed
 * `applicant_verbatim`, which is a claim the paired-fixture rules (§7's T-BAN-1, T-VERB-1) hold to account.
 */
function typeSlot(v: SlotValue, source: CopySource, id: string | undefined, defaultSlotSource?: CopySource): TypedSlot {
  return typeof v === 'string' ? { source: defaultSlotSource ?? source, id, text: v } : v;
}

/**
 * T-BAN-2s OVER SLOT VALUES — every value inserted into a template is scanned, REGARDLESS of provenance class,
 * with the single §12.6 part-0 exemption (spec rev 14, closing the review's finding 5).
 *
 * WHAT WENT WRONG BEFORE. `TemplateText` scanned the registered TEMPLATE and nothing else, then inserted every
 * slot value verbatim. Slot values are not template text — they are ids, release names, counts, band labels,
 * placeholder literals — and several of them are `opaque_metadata` or `copy` under §1.2b, which T-BAN-1 puts
 * squarely IN scope. So a poisoned system value (the review's worked example: a `profile_release_id` reading
 * "strong candidate", reaching the screen through `CS-OVERLAY-SOURCE`) rendered with the whole registry looking
 * the other way. The DOM sweep would only have caught it if a fixture happened to carry one; runtime behaviour
 * did not block it at all, which is precisely what T-BAN-2s says must not be true.
 *
 * THE RULE NOW: an `applicant_verbatim` slot is exempt — the applicant may legitimately have written "strong
 * candidate", and their own words must come back unaltered — and EVERY other slot is scanned as copy, because
 * every other slot is something this system produced. A violating slot BLOCKS THE WHOLE RENDER, fail-closed, the
 * same as a violating template: half a sentence is not a safer sentence.
 */
function scanSlot(typed: TypedSlot): boolean {
  if (typed.source === 'applicant_verbatim') return true; // §12.6 part 0 — the typed exemption, by provenance
  return scanSystemText(typed.text, typed.id).ok;
}

/**
 * Render a registered template, segment by segment, each segment stamped with its OWN provenance.
 *
 * WHY SEGMENTS AND NOT ONE STRING. A suggested-phrasing render is literally a system-authored scaffold with
 * applicant values dropped into it. Emitting it as one string would mark the applicant's own words as
 * system-authored copy — which is exactly the laundering §12.6 warns about, in reverse: the banned-output scanner
 * would then fail the build on a phrase the applicant wrote. Segmenting keeps each half under its own rule.
 */
export function TemplateText({ source, id, template, slots = {}, defaultSlotSource }: TemplateTextProps) {
  // T-BAN-2s — A REGISTRY VIOLATION BLOCKS THE RENDER, and does not render with a warning. The template is
  // system-authored text by definition (it comes from a registry), so it is scanned whole, before segmentation:
  // a banned phrase split across two literal segments is still a banned phrase on the screen.
  if (!scanSystemText(template, id).ok) return null;
  // AND SO IS EVERY SLOT VALUE, before any of them renders. Scanned up front rather than per segment, so a
  // violation in the LAST slot cannot leave the first three already on the screen (§7's fail-closed rule is about
  // the render, not about the offending fragment).
  for (const v of Object.values(slots)) {
    if (!scanSlot(typeSlot(v, source, id, defaultSlotSource))) return null;
  }
  const segments = segmentTemplate(template);
  return (
    <span data-copy-src={source} data-copy-id={id}>
      {segments.map((seg, i) => {
        if (seg.kind === 'literal') {
          // The literal halves of the template belong to the template's own source and id.
          return (
            <span key={i} data-copy-src={source} data-copy-id={id}>
              {seg.text}
            </span>
          );
        }
        const v = slots[seg.name];
        if (v === undefined) {
          // Unfilled: the slot renders LITERAL, and it is still template text, so it keeps the template's source.
          return (
            <span key={i} data-copy-src={source} data-copy-id={id}>
              {`{${seg.name}}`}
            </span>
          );
        }
        const typed: TypedSlot = typeSlot(v, source, id, defaultSlotSource);
        // A SLOT VALUE IS NOT TEMPLATE TEXT, and the DOM says so: `data-copy-slot` names the slot it filled, so
        // the copy sweep can tell "this string must be a literal segment of a registered template" from "this
        // string is a value that landed in one" without re-parsing anything.
        return (
          <span
            key={i}
            data-copy-src={typed.source}
            data-copy-id={typed.id}
            data-copy-slot={seg.name}
            // A registered placeholder renders VISIBLY-PLACEHOLDER (§4.9): bracketed — the literal itself carries
            // the brackets — and muted, so "we do not know this" reads as a gap to fill rather than as a fact.
            style={typed.source === 'placeholder_literal' ? { color: 'var(--muted)' } : undefined}
          >
            {typed.text}
          </span>
        );
      })}
    </span>
  );
}

/** (1) A registered `CS-*` string. The ONLY way this surface renders codebook copy. */
export function Copy({
  id,
  slots,
  defaultSlotSource,
}: {
  id: CopyStringId;
  slots?: Readonly<Record<string, SlotValue>>;
  defaultSlotSource?: CopySource;
}) {
  return (
    <TemplateText source="cs" id={id} template={COPY[id]} slots={slots} defaultSlotSource={defaultSlotSource} />
  );
}

/** The engine's own `RenderedString` shape — `{copy_string_id, text}`, already filled at compute time. */
export interface RenderedString {
  copy_string_id: string;
  text: string;
}

/**
 * (3) An engine-pre-rendered string, VERBATIM.
 *
 * R5-OQ-E's single-copy-authority rule: `renderPrevalence` has already filled `{criterion}`/`{n}`/`{N}` from the
 * owner-reviewed profile release before this row ever reached the client. There is no client-side re-fill, and no
 * `PC-*` re-resolution step. The pair renders as-is.
 */
export function EngineRendered({ value }: { value: RenderedString }) {
  // System-authored by construction (its copy authority is the codebook/profile release the engine stamped in),
  // so it is scanned exactly like a client-resolved template, and a violation blocks the row's text rather than
  // annotating it.
  if (!scanSystemText(value.text, value.copy_string_id).ok) return null;
  return (
    <span data-copy-src="engine-rendered" data-copy-id={value.copy_string_id}>
      {value.text}
    </span>
  );
}

/**
 * (4) Applicant text — verbatim, never templated, never a render source for a `{slot}`.
 *
 * Exempt from the banned-output sweep by the §12.6 typed exemption (an applicant may legitimately have written
 * "strong candidate" in their own CV, and their own words must come back unaltered). The exemption's integrity is
 * T-VERB-1's job, not this component's.
 */
export function ApplicantText({ text }: { text: string }) {
  return <span data-copy-src="applicant_verbatim">{text}</span>;
}

/**
 * (6) A registered Appendix A.4 placeholder literal, as a TYPED SLOT.
 *
 * MEMBERSHIP IS ENFORCED AT RENDER, not merely upstream: an unapproved literal returns null, which blocks the
 * whole phrasing render at the call site, because `checkPhrasingRenderable`'s `unapproved_placeholder` condition
 * and this one must agree even if a future caller forgets to run the check.
 *
 * The `data-copy-id` carries the `PL-*` ID, derived from the registry rather than re-declared so the id↔literal
 * map cannot drift — and because only ids, never applicant-facing text, may go in an attribute (rule 2).
 */
export function placeholderSlot(literal: string): TypedSlot | null {
  if (!APPROVED_PLACEHOLDER_LITERALS.includes(literal)) return null;
  const id = Object.entries(APPROVED_PLACEHOLDERS).find(([, v]) => v === literal)?.[0];
  return { source: 'placeholder_literal', id, text: literal };
}

/**
 * (5) Suggested phrasing: a registered `PHRASING_TEMPLATES` scaffold with typed slots.
 *
 * The caller is responsible for having passed `checkPhrasingRenderable` FIRST (§4.9's full T-AF-2 seven-condition
 * check) — this renderer additionally refuses an unregistered template id, because an unregistered template is
 * unreviewed text and there is no rendering of it that would be correct.
 */
export function PhrasingText({
  templateId,
  slots,
}: {
  templateId: string;
  slots: Readonly<Record<string, TypedSlot>>;
}) {
  const template = (PHRASING_TEMPLATES as Record<string, string | undefined>)[templateId];
  if (template === undefined) return null;
  return <TemplateText source="phrasing_template" id={templateId} template={template} slots={slots} />;
}

/* ============================================================================================================== *
 * THE UNIFORM BAND CHIP (§1.4, T-UNIFORM-1)
 *
 * ONE component, ONE frozen style object, NO per-value modifier, NO icon — on every facet level, domain band,
 * presentation band, and overlay documentation class. "Substantial" is typographically indistinguishable from
 * "Not shown" except in its label text. The style object is shared BY REFERENCE, which is what makes computed-
 * style equality across band values a property of the code rather than of somebody's diligence.
 * ============================================================================================================== */

export function BandChip({ id, value }: { id?: CopyStringId; value?: RenderedString }) {
  // The LABEL goes through `Copy` (or `EngineRendered`) like every other string — a band label is not a special
  // kind of text, it is a registered string, and routing it around the chokepoint is how one would eventually
  // stop being scanned. The chip element itself carries only the treatment, which is why the treatment can be
  // compared across values.
  //
  // TWO LABEL SOURCES, ONE TREATMENT (spec rev 14, closing the review's finding 6 on T-UNIFORM-1). Content bands,
  // presentation bands and facet levels name their label by a registered `CS-*` id. The OVERLAY's band-like value
  // — the row's `documentation_class` — has no separate label id anywhere in Appendix A, and this surface invents
  // none; what it has is the engine's own `RenderedString` for that class, already filled from the owner-reviewed
  // release at compute time (R5-OQ-E's single copy authority). So the chip accepts either, and the treatment is
  // identical by construction: ONE frozen style object, shared by reference, with no per-value modifier and no
  // icon slot to fill. That is what puts the overlay inside T-UNIFORM-1's `[data-rubric-band]` sweep instead of
  // beside it.
  return (
    <span className="rubric-band" data-rubric-band="" style={BAND_CHIP_STYLE}>
      {id !== undefined ? <Copy id={id} /> : value !== undefined ? <EngineRendered value={value} /> : null}
    </span>
  );
}

/* ============================================================================================================== *
 * LAYER SECTION HEADINGS (accessibility audit A11Y-1, round 3)
 *
 * §4's layers each render as their own `<section>`, and most of them had no accessible name and no heading at
 * all — so a screen-reader user navigating by landmark or by heading could not tell which layer they were in.
 * Each layer now carries a real `<h2>` of registered copy (A.5-P.9) and names its section by referring to it.
 *
 * TWO RULES, BOTH LOAD-BEARING:
 *   * `aria-labelledby`, NEVER `aria-label`. A label attribute is a rendered string with nowhere to declare its
 *     copy id, and T-COPY-1 does not stop applying to a string because it is only ever spoken.
 *   * The DOM id is DERIVED from the copy id, in one place, so the heading and the reference cannot drift apart
 *     into a section named by an element that does not exist.
 *
 * VISUALLY HIDDEN: the accessibility tree is what was missing; the visual layout is owner-frozen, and adding
 * nine visible headings to it would be a redesign smuggled in behind an accessibility fix. `IntendedUse` set
 * this pattern in R5.2.
 * ============================================================================================================== */

/** The DOM id for a layer heading — one derivation, shared by the heading and by every `aria-labelledby`. */
export function layerHeadingId(id: CopyStringId): string {
  return `rubric-heading-${id.toLowerCase()}`;
}

/** A layer's visually-hidden `<h2>`. Its `id` is what the enclosing `<section>` points `aria-labelledby` at. */
export function LayerHeading({ id }: { id: CopyStringId }) {
  return (
    <h2 id={layerHeadingId(id)} style={SR_ONLY}>
      <Copy id={id} />
    </h2>
  );
}

/* ============================================================================================================== *
 * EVIDENCE REVEAL (§4.2 / §4.1's `CS-SHOW-SOURCE` affordance)
 * ============================================================================================================== */

/** One resolved piece of evidence: the fact's own field and the applicant text behind it. */
export interface ResolvedEvidence {
  fact_id: string;
  field: string;
  /** The applicant's own words — an `EvidenceSpan.quoted_text`, or a `user_supplied` value they typed. */
  quoted_text: string;
  /**
   * WHICH OF THOSE TWO IT IS (accessibility audit A11Y-3, round 3) — and therefore whether it can stand alone.
   *
   * A `'span'` is a clause lifted out of the applicant's document: it carries its own context, because it is
   * their sentence. A `'value'` is a stored FIELD VALUE — a normalized token, or a correction the applicant
   * typed into one field — and on its own it is not evidence of anything a reader can follow. The audit found
   * the consequence on a real render: a reveal listing nine facts read out as "stated, stated, stated, …",
   * because nine §2.2a signal facts on one entry each stored the same value and nothing said which signal.
   *
   * So a `'value'` renders behind its registered field label and a `'span'` renders alone. Carried as data
   * rather than guessed from the text, because "does this look like a sentence?" is not a decidable question.
   */
  kind: 'span' | 'value';
}

/**
 * The "why" behind a level, a band, or a sub-element: the quoted span(s) that established it.
 *
 * A `<details>` element, deliberately: it is keyboard-operable and screen-reader-announced with no JavaScript and
 * no ARIA of our own (T-A11Y-1). Renders NOTHING when no cited fact resolves — an affordance that opens onto an
 * empty panel is a promise the payload could not keep, and inventing a span to fill it is unthinkable.
 */
export function EvidenceReveal({ evidence, label }: { evidence: ResolvedEvidence[]; label?: ReactNode }) {
  if (evidence.length === 0) return null;
  return (
    <details className="rubric-evidence">
      <summary style={EVIDENCE_SUMMARY_STYLE}>{label ?? <Copy id="CS-SHOW-SOURCE" />}</summary>
      <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
        {evidence.map((e) => (
          <li key={e.fact_id} style={EVIDENCE_QUOTE_STYLE}>
            <EvidenceLine evidence={e} />
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * ONE EVIDENCE ITEM (A11Y-3, round 3).
 *
 * A SPAN STANDS ALONE — it is the applicant's own clause, and prefixing it with a field name would be this
 * surface talking over their sentence.
 *
 * A STORED VALUE DOES NOT. It renders behind its registered field label, so a list of them reads as
 * "Participatory function: stated" rather than as "stated" nine times over. The label is looked up through the
 * SAME `factLabels` registry Layer 1 offers editors from — not a second table — and an unregistered field falls
 * back to the bare value, which is this surface's standing rule everywhere: an affordance we cannot label
 * degrades, and never prints an internal token like `signal.participatory_function` at somebody (§0/T-COPY-1).
 */
function EvidenceLine({ evidence }: { evidence: ResolvedEvidence }) {
  const labelId = evidence.kind === 'value' ? presentationFor(evidence.field)?.labelId : undefined;
  if (labelId === undefined) return <ApplicantText text={evidence.quoted_text} />;
  return (
    <>
      {/* THE LABEL AND THE VALUE ARE SEPARATED BY LAYOUT, NOT BY PUNCTUATION. A literal ": " would be a rendered
          text node belonging to no registered string, which T-COPY-1 forbids and which is not worth an exemption:
          the muted label already reads as a label, and a screen reader gets the two as separate phrases either
          way. The `{' '}` between them is whitespace, which the copy sweep skips. */}
      <span style={{ ...MUTED_STYLE, margin: 0 }}>
        <Copy id={labelId} />
      </span>
      {' '}
      <ApplicantText text={evidence.quoted_text} />
    </>
  );
}

/** A visually-hidden label, for the places where the visual layout carries meaning a screen reader cannot see. */
export function ScreenReaderOnly({ children }: { children: ReactNode }) {
  return <span style={SR_ONLY}>{children}</span>;
}
